"use client";

import debounce from "lodash.debounce";
import { useCallback, useEffect, useState } from "react";
import type { SizeType } from "../../types";

export type Breakpoint = {
  size: SizeType;
  value: number;
};
export type BreakpointsType = Breakpoint[];
export type UseBreakpointType = {
  breakpoint: Breakpoint;
  up: (size: SizeType) => boolean | undefined;
  down: (size: SizeType) => boolean | undefined;
};

// Hard code breakpoints for SB designs
export const DEFAULT_BREAKPOINTS = [
  { size: "xs", value: 320 },
  { size: "sm", value: 480 },
  { size: "md", value: 670 },
  { size: "lg", value: 960 },
  { size: "xl", value: 1200 },
  { size: "xxl", value: 1320 },
] as BreakpointsType;

const getBreakpoint = (breakpoints: BreakpointsType, windowWidth: number) => {
  // When breakpoint size and windowWidth are a match
  // The addition of 1px ensures the right breakpoint
  const closest = breakpoints.findIndex(
    breakpoint => breakpoint.value >= windowWidth + 1,
  );
  const smallest = breakpoints[0];
  const largest = breakpoints[breakpoints.length - 1];
  const match = breakpoints[closest - 1];

  if (closest === 0) return smallest;
  if (closest === -1) return largest;

  return match;
};

const useBreakpoint = (
  customBreakpoints?: BreakpointsType,
): UseBreakpointType => {
  const window: Window = globalThis?.window || undefined;
  const isClientSide = typeof window !== "undefined";
  const breakpoints = customBreakpoints || DEFAULT_BREAKPOINTS;
  const defaultBreakpoint = breakpoints[0];
  const [windowWidth, setWindowWidth] = useState<number>(
    isClientSide ? window.innerWidth : defaultBreakpoint.value,
  );
  const [currentBreakpoint, setCurrentBreakpoint] =
    useState<Breakpoint>(defaultBreakpoint);
  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    if (!windowWidth || breakpoints.length === 0) {
      setCurrentBreakpoint(breakpoints[0]);
      return;
    }

    const newBreakpoint = getBreakpoint(breakpoints, windowWidth);

    // Don't update state if value is the same
    if (currentBreakpoint.size === newBreakpoint?.size) return;

    setCurrentBreakpoint(newBreakpoint);
  }, [currentBreakpoint, windowWidth, breakpoints]);

  useEffect(() => {
    window.addEventListener("resize", debounce(handleResize, 200), {
      passive: true,
    });

    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  /**
   * up(breakpointSize: string) => boolean
   * Returns true if the current screen width >= breakpoint width
   */
  const up = useCallback(
    (size: SizeType) => {
      const sizeIndex = breakpoints.findIndex(item => item.size === size);
      if (sizeIndex === -1) {
        return false;
      }
      const currentIndex = breakpoints.findIndex(
        item => item.size === currentBreakpoint.size,
      );

      return currentIndex >= sizeIndex;
    },
    [currentBreakpoint, breakpoints],
  );

  /**
   * down(breakpointSize: string) => boolean
   * Returns true if the current screen width <= breakpoint width
   */
  const down = useCallback(
    (size: SizeType) => {
      const sizeIndex = breakpoints.findIndex(item => item.size === size);
      if (sizeIndex === -1) {
        return true;
      }
      const currentIndex = breakpoints.findIndex(
        item => item.size === currentBreakpoint.size,
      );

      return currentIndex <= sizeIndex;
    },
    [currentBreakpoint, breakpoints],
  );

  return {
    breakpoint: isClientSide ? currentBreakpoint : defaultBreakpoint,
    up,
    down,
  };
};

export { useBreakpoint };
